import { mapHead } from "../madmonkey/routeHead";
import { MAP_HOSTEL_SLUGS } from "../madmonkey/routeIndex";
import { partnerAsset } from "../shared/assets";
import type { PartnerHead } from "../shared/partnerHead";

/** A head as the file has it, and the media folder its card image is in when that is not the slug: the Dutch page shares the English one's photos */
type HeadEntry = PartnerHead & { media?: string };

/**
 * The little the route table needs to know about the partner map pages: which slugs exist, and what to put in each
 * head. Kept apart from `registry.ts` on purpose: `marketingRoutes.tsx` is part of the entry every marketing page
 * loads, and the pages' content (places, photos, the week program) is not. `registry.test.ts` checks this against
 * each content file's `head`, so the two cannot drift. `ogImage` is a media path here, as in the file.
 * No em dashes in a new title.
 *
 * The Mad Monkey hostels' maps (`/p/mad-monkey-<hostel>`) are not in `HEADS`: they are made from the hostel data, and so are their
 * heads (`madmonkey/routeHead.ts`, from the same light index as the hostel pages' heads, whose `ogImage` is a full URL already).
 */
const HEADS: Record<string, HeadEntry> = {
	"urban-jungle": {
		title: "Stippl × The Urban Jungle Hostel: Málaga map and week program",
		description:
			"Eight highlights around The Urban Jungle Hostel in Málaga's old town, picked by the crew, plus what is on at the hostel every day of the week.",
		ogImage: "photos/hostel-hostel-photos0.jpg",
	},
	indewaag: {
		title: "Stippl × In de Waag: Amsterdam from the Waag",
		description: "Fourteen Amsterdam classics, all walkable from In de Waag on the Nieuwmarkt, plus where to eat and drink at the Waag.",
		ogImage: "photos/og.jpg",
	},
	"indewaag-nl": {
		title: "Stippl × In de Waag: Amsterdam vanuit de Waag",
		description: "Veertien Amsterdamse klassiekers op loopafstand van In de Waag op de Nieuwmarkt, plus waar je bij de Waag kunt eten en drinken.",
		ogImage: "photos/og.jpg",
		media: "indewaag",
	},
	odeaandeamstel: {
		title: "Stippl × Ode aan de Amstel: Amsterdam from the water",
		description: "Fifteen Amsterdam favourites, a short walk or bike ride from Ode aan de Amstel's terrace on the Amstel, plus what is on the menu at Ode.",
		ogImage: "photos/og.jpg",
	},
	// Viajero CDMX Centro: hidden until Viajero agrees (registry.ts HIDDEN_PARTNER_MAPS); uncomment to publish /p/viajero-mexico-cdmx.
	// "viajero-mexico-cdmx": {
	// 	title: "Stippl × Viajero CDMX Centro: Mexico City map and experiences",
	// 	description:
	// 		"Ten highlights around Viajero CDMX Centro in Mexico City's historic centre, plus Viajero's own tours and nights and the day trips worth the ride.",
	// 	ogImage: "photos/hostel-0.jpg",
	// },
};

const MAD_MONKEY_PREFIX = "mad-monkey-";

export const PARTNER_MAP_SLUGS: string[] = [...Object.keys(HEADS), ...MAP_HOSTEL_SLUGS.map((hostel) => `${MAD_MONKEY_PREFIX}${hostel}`)];

export function partnerMapHead(slug: string): PartnerHead | null {
	if (Object.prototype.hasOwnProperty.call(HEADS, slug)) {
		const { media, ...head } = HEADS[slug];
		return { ...head, ogImage: partnerAsset(media ?? slug, head.ogImage) };
	}
	return slug.startsWith(MAD_MONKEY_PREFIX) ? mapHead(slug.slice(MAD_MONKEY_PREFIX.length)) : null;
}

export const isKnownPartnerMap = (slug: string): boolean => partnerMapHead(slug) !== null;
